<template>
  <div class="container-fluid py-4 night-audit-page">
    <!-- Page Title -->
    <div class="d-flex justify-content-between align-items-center mb-4">
      <h3 class="mb-0 font-weight-bold">Night Audit</h3>
      <div>
        <input
          type="date"
          class="form-control"
          v-model="selectedDate"
          @change="fetchAudit"
        />
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="text-center py-5">
      <div class="spinner-border text-primary" role="status"></div>
      <p class="mt-2 text-muted">Loading night audit...</p>
    </div>

    <!-- Error State -->
    <div v-if="error" class="alert alert-danger">
      {{ error }}
    </div>

    <!-- Audit Data -->
    <div v-if="auditData && !loading" class="audit-content">
      <!-- Date and Run Info -->
      <div class="card mb-4 shadow-sm">
        <div class="card-body d-flex justify-content-between align-items-center">
          <div>
            <h5 class="mb-0">Audit for {{ auditData.date }}</h5>
            <small class="text-muted">
              Run at: {{ auditData.audit_meta.run_at }} by {{ auditData.audit_meta.run_by }}
            </small>
          </div>
        </div>
      </div>

      <!-- Room Overview -->
     <div class="card">
            <div class="card-body">
                <div class="row">
                    <div class="col-md-4 col-sm-12">
                        <h4 class="font-weight-bold h4">Rooms Overview</h4>
                    <ul class="list-group list-group-flush">
                        <li class="m-1 py-1 border-bottom border-light" v-for="(value, key) in auditData.rooms" :key="key">

                        <p class="text-uppercase text-muted">{{ formatLabel(key) }} : <span class="badge badge-secondary py-1 px-1">{{ value }}</span></p>



                    </li>
                    </ul>
                    </div>
                    <div class="col-md-4 col-sm-12">
                        <h4 class="font-weight-bold h4">Revenues</h4>
                    <ul class="list-group list-group-flush">
                         <li class="m-1 py-1 border-bottom border-light" v-for="(value, key) in auditData.revenue" :key="key">

                        <p class="text-uppercase text-muted">{{ formatLabel(key) }} : <span class="badge badge-secondary py-1 px-1">{{ convertCurrencyToLocal(value) }}</span></p>



                    </li>
                    </ul>
                    </div>
                </div>


      </div>
      </div>



      <!-- Payments Section -->
      <div class="card mb-4 shadow-sm">
        <div class="card-header bg-light">
          <h6 class="mb-0 font-weight-bold">Payments</h6>
        </div>
        <div class="card-body">
          <div class="row text-center">
            <div class="col-md-4">
              <h6 class="text-muted">Incoming Total</h6>
              <h4 class="font-weight-bold text-success">
                KSH {{ formatCurrency(auditData.payments.incoming_total) }}
              </h4>
            </div>
            <div class="col-md-4">
              <h6 class="text-muted">Refunds</h6>
              <h4 class="font-weight-bold text-danger">
                KSH {{ formatCurrency(auditData.payments.refunds_total) }}
              </h4>
            </div>
            <div class="col-md-4">
              <h6 class="text-muted">By Method</h6>
              <ul class="list-unstyled mb-0">
                <li
                  v-for="(amount, method) in auditData.payments.by_method"
                  :key="method"
                  class="small"
                >
                  {{ method }}: <strong>KSH {{ formatCurrency(amount) }}</strong>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <!-- Exceptions Section -->
      <div class="card mb-4 shadow-sm">
        <div class="card-header bg-light">
          <h6 class="mb-0 font-weight-bold">Exceptions</h6>
        </div>
        <div class="card-body row text-center">
          <div class="col-md-4">
            <h6 class="text-muted">Discounts Total</h6>
            <h4 class="font-weight-bold">KSH {{ formatCurrency(auditData.exceptions.discounts_total) }}</h4>
          </div>
          <div class="col-md-4">
            <h6 class="text-muted">Refunds Total</h6>
            <h4 class="font-weight-bold">KSH {{ formatCurrency(auditData.exceptions.refunds_total) }}</h4>
          </div>
          <div class="col-md-4">
            <h6 class="text-muted">No-Shows Flagged</h6>
            <h4 class="font-weight-bold">{{ auditData.exceptions.no_shows_flagged }}</h4>
          </div>
        </div>
      </div>

      <!-- Counts Summary -->
      <div class="card mb-4 shadow-sm">
        <div class="card-header bg-light">
          <h6 class="mb-0 font-weight-bold">Summary</h6>
        </div>
        <div class="card-body row text-center">
          <div class="col-md-6">
            <h6 class="text-muted">Reservations Processed</h6>
            <h4 class="font-weight-bold">{{ auditData.counts.reservations_processed }}</h4>
          </div>
          <div class="col-md-6">
            <h6 class="text-muted">Orders Count</h6>
            <h4 class="font-weight-bold">{{ auditData.counts.orders_count }}</h4>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  data() {
    return {
      selectedDate: new Date().toISOString().slice(0, 10), // default to today
      auditData: null,
      loading: false,
      error: null
    }
  },
  created() {
    this.fetchAudit()
  },
  methods: {
    async fetchAudit() {
      this.loading = true
      this.error = null
      this.auditData = null

      try {
        const response = await axios.post(`/api/reservations/night-audit`)



        this.auditData = response.data
      } catch (err) {
        this.error = err.message
      } finally {
        this.loading = false
      }
    },
    formatCurrency(amount) {
      return amount.toLocaleString('en-KE', { minimumFractionDigits: 2 })
    },
    formatLabel(key) {
      return key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
    }
  }
}
</script>

<style scoped>
.night-audit-page {
  background: #f8f9fa;
  min-height: 100vh;
}

.card {
  border: none;
  border-radius: 0.75rem;
}

.card-header {
  border-bottom: none;
}

.card-body h4 {
  color: #343a40;
}

.spinner-border {
  width: 3rem;
  height: 3rem;
}
</style>
